/**
 * Shape coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_coloring_themes`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const shapeColoringPagesPack: ToilePack = {
    slug: 'shape-coloring-pages',
    category: 'coloring',
    name: 'Shape coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'chart', label: 'Shape chart', subject: 'a grid of eight shapes in panels, each named below: "circle", "square", "triangle", "rectangle", "oval", "star", "heart", "diamond"'},
        {key: 'animals', label: 'Shape animals', subject: 'an owl, a fish and a cat each built from circles, triangles and rectangles with the shapes clearly separate'},
        {key: 'find', label: 'Find the shapes', subject: 'a simple street scene where houses, windows, wheels and a sun are made of plain shapes for a child to find and color'},
    ],
    styles: [
        {key: 'bold', label: 'Big and simple', style: 'thick black outline, big plain shapes, made for toddlers', palette: 'black ink only', framing: 'The page centered with a wide white margin'},
        {key: 'cheerful', label: 'Cheerful faces', style: 'medium black outline, smiling faces and big eyes on the shapes and the animals', palette: 'black ink only', framing: 'The page centered with a white margin'},
        {key: 'tidy', label: 'Tidy worksheet', style: 'thin neat black line, evenly spaced shapes as on a preschool worksheet', palette: 'black ink only', framing: 'The page laid out inside a thin box border with a white margin'},
    ],
    directions: ['robots', 'trains', '3D shapes'],
    directionHint: 'try: robots',
    seo: {
        title: 'Shape coloring pages: chart, shape animals, find shapes',
        metaDescription: 'Nine shape coloring pages: a labelled shape chart, animals built from shapes and a picture with hidden shapes to find.',
        h1: 'Shape coloring pages: shape chart, shape animals, find the shapes',
        tagline: 'Basic shapes named on a chart, animals built from them and shapes to find.',
        intro: 'Shape coloring pages for young children learning their shapes at home and in preschool. The nine pages show a chart of basic shapes with their names, animals built from shapes, and a picture to find the hidden shapes in. The styles are big and simple, cheerful faces and tidy worksheet. Type a different subject, such as robots or 3D shapes, and the same style carries it.',
        useCases: [
            'a shapes lesson in a preschool classroom',
            'a shape hunt activity at home',
            'a reference chart on a classroom wall',
            'a practice sheet for a child learning shape names',
        ],
        faq: [
            {q: 'Which shapes are on the chart?', a: 'A chart of basic shapes, each with its name written beside it.'},
            {q: 'What is the difference between the styles?', a: 'Big and simple uses thick outlines and large plain shapes for toddlers. Cheerful faces adds smiles to the shapes and animals, and the tidy worksheet style uses thin, evenly spaced shapes.'},
            {q: 'Can I ask for a different subject?', a: 'Yes. A subject you type, such as robots, trains or 3D shapes, is a request; the page keeps the same style but may not match it exactly.'},
            {q: 'Are the pages in color?', a: 'No. Each page is a black line drawing on white paper, ready to print and color.'},
        ],
    },
}
